import Path from "@app/enum/Path";

// Pages that must never be restored after login. Sending the user back to an auth screen
// would bounce them straight to the login form again, and saving one would overwrite a
// genuinely useful target — a stale 401 can ask for login while the user already sits on
// /login.
const nonRestorablePaths: readonly Path[] = [
  Path.AUTH_CALLBACK,
  Path.EMAIL_UNSUBSCRIBE,
  Path.FORGOT_PASSWORD,
  Path.LOGIN,
  Path.MANAGE_USER_ACCESS,
  Path.REGISTER,
  Path.RESET_PASSWORD,
  Path.SSO_LOGIN,
];

/**
 * Whether a path may be navigated to after the user logs in.
 *
 * Also closes an open redirect: a protocol-relative value such as "//example.com" would
 * otherwise reach the router as an absolute URL. In practice `history.replaceState` refuses a
 * cross-origin URL and throws, so this guards against a broken restore (the poisoned value
 * would survive to throw again on every later login) rather than an actual off-site hop, and
 * `?mmc_redirect=` is stored straight from the query string without any validation.
 */
const isRestorableLocation = (path: string): boolean => {
  // The URL parser reads "\" as "/" and strips tab, line feed, and carriage return before
  // parsing, so "/\evil.com" and "/%0A/evil.com" resolve to another origin exactly like
  // "//evil.com" does. URLSearchParams.get percent-decodes, so ?mmc_redirect= delivers these
  // as literal characters.
  if (!path.startsWith("/") || /^\/[/\\]/.test(path) || /[\t\n\r\\]/.test(path)) {
    return false;
  }

  // Case-insensitive to match react-router's default (non-sensitive) route matching: "/Login"
  // matches <AuthRoute path={Path.LOGIN}> just as "/login" does, so this must reject it too.
  const pathname = path.split(/[?#]/)[0].toLowerCase();

  // Compare Path.ROOT for equality, never by prefix: "/" is a prefix of every path, so a
  // prefix test here would reject everything.
  if (pathname === Path.ROOT) {
    return false;
  }

  return !nonRestorablePaths.some(
    (nonRestorablePath) =>
      pathname === nonRestorablePath || pathname.startsWith(`${nonRestorablePath}/`)
  );
};

export default isRestorableLocation;
